"use client";

import * as React from "react";
import { useCallback, useRef, useState } from "react";

import CloseButton from "@/components/flowbite/button/close-button";
import { SideModal } from "@/components/flowbite/side-modal";
import { cn } from "@/shared/helpers/cn.helper";

import { useMapPanelContext } from "./map-panel-context";
import { MapPanelIndicator } from "./map-panel-indicator";

const MIN_WIDTH = 280;
const MAX_WIDTH = 600;
const DEFAULT_WIDTH = 342;

interface MapPanelSideMenuProps {
  className?: string;
  /** "fit" = menu height only covers content (e.g. Find My School). Default = full height. */
  contentHeight?: "full" | "fit";
  /** When provided, disables resize and uses a fixed Tailwind width class instead. */
  widthClassName?: string;
}

export function MapPanelSideMenu({
  className,
  contentHeight = "full",
  widthClassName,
}: MapPanelSideMenuProps) {
  const { tabs, activeKey, open, setOpen, side, setActiveKey } = useMapPanelContext();
  const [width, setWidth] = useState(DEFAULT_WIDTH);
  const dragStartX = useRef<number | null>(null);
  const dragStartWidth = useRef<number>(DEFAULT_WIDTH);

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    dragStartX.current = e.clientX;
    dragStartWidth.current = width;

    const onMouseMove = (moveEvent: MouseEvent) => {
      if (dragStartX.current === null) return;
      const delta = moveEvent.clientX - dragStartX.current;
      const next = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, dragStartWidth.current + delta));
      setWidth(next);
    };

    const onMouseUp = () => {
      dragStartX.current = null;
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  }, [width]);
  const activeTab = tabs.find((t) => t.key === activeKey);
  if (!activeTab) return null;

  const heightClass = contentHeight === "fit" ? "h-fit" : "h-[calc(100%-2rem)]";

  return (
    <SideModal
      className={cn(heightClass, className)}
      show={open}
      side={side}
      widthClassName={widthClassName}
      contentStyle={widthClassName ? undefined : { width }}
      onResizeMouseDown={widthClassName ? undefined : onMouseDown}
    >
      <div className="flex h-full w-full gap-4">

        <div className="flex flex-col gap-6">
          {tabs.map((tab) => {
            const isActive = tab.key === activeKey;
            const IconComponent = React.isValidElement(tab.icon)
              ? (tab.icon.type as React.ElementType)
              : null;

            return (
              <div
                key={tab.key}
                className="relative"
              >
                <button
                  className={cn(
                    "w-full justify-start rounded-lg p-2", 
                    {
                      "bg-background-primary text-primary": isActive,
                      "text-btn-icon": !isActive,
                    },
                  )}
                  onClick={() => {
                    setActiveKey(tab.key);
                    setOpen(true);
                  }}
                >
                  {IconComponent && (
                    <IconComponent className={cn("h-6 w-6", {
                      "text-light-600": isActive,
                      "text-light-500": !isActive,
                    })}
                    />
                  )}
                  {tab.indicator && (
                    <MapPanelIndicator indicator={tab.indicator} />
                  )}
                </button>
              </div>
            );
          })}
        </div>
        <div
          className={cn(
            "flex min-w-0 flex-col gap-3",
            contentHeight === "full" && "grow",
          )}
        >
          {activeTab.label ? (
            <h3 className="text-light-500 text-lg font-semibold">
              {activeTab.label}
            </h3>
          ) : null}
          {activeTab.content}
        </div>
        <CloseButton
          className="absolute right-3 top-3"
          onClose={() => setOpen(false)}
        />
      </div>
    </SideModal>
  );
}